import React, { forwardRef, useEffect } from 'react';
import { Box, BoxProps } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { ChildrenProp } from '../declarations/ChildrenProp';

type ContainerSpacingProps = {
  m?: number | string;
  mt?: number | string;
  mb?: number | string;
  ml?: number | string;
  mr?: number | string;
  mx?: number | string;
  my?: number | string;
  p?: number | string;
  pt?: number | string;
  pb?: number | string;
  pl?: number | string;
  pr?: number | string;
  px?: number | string;
  py?: number | string;
};

export interface ContainerProps
  extends ChildrenProp,
    ContainerSpacingProps,
    Partial<
      Pick<
        BoxProps,
        'component' | 'sx' | 'onClick' | 'onMouseOver' | 'onMouseEnter' | 'onMouseLeave' | 'role' | 'className'
      >
    > {
  column?: boolean;
  wrap?: boolean;
  spaceBetween?: boolean;
  gap?: number | string;
  top?: boolean;
  bottom?: boolean;
  left?: boolean;
  right?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  debugBorder?: boolean;
}

const Container = forwardRef<HTMLElement, ContainerProps>(
  (
    {
      children,
      column = false,
      wrap = false,
      spaceBetween = false,
      gap = 1,
      top = false,
      bottom = false,
      left = false,
      right = false,
      fullWidth = false,
      fullHeight = false,
      debugBorder = false,
      sx,
      m,
      mt,
      mb,
      ml,
      mr,
      mx,
      my,
      p,
      pt,
      pb,
      pl,
      pr,
      px,
      py,
      ...boxProps
    },
    ref,
  ) => {
    let justifyContent: 'flex-start' | 'center' | 'flex-end' | 'space-between' = 'center';
    let alignItems: 'flex-start' | 'center' | 'flex-end' = 'center';

    if (left && right) {
      throw new Error('An element cannot be on the left and right side at the same time...');
    }

    if (top && bottom) {
      throw new Error('An element cannot be at the top and bottom at the same time...');
    }

    if (spaceBetween && ((!column && (left || right)) || (column && (top || bottom)))) {
      throw new Error(`Space between will override ${column ? 'top/bottom' : 'left/right'}`);
    }

    if (column) {
      if (top) {
        justifyContent = 'flex-start';
      } else if (bottom) {
        justifyContent = 'flex-end';
      }

      if (left) {
        alignItems = 'flex-start';
      } else if (right) {
        alignItems = 'flex-end';
      }
    } else {
      if (top) {
        alignItems = 'flex-start';
      } else if (bottom) {
        alignItems = 'flex-end';
      }

      if (left) {
        justifyContent = 'flex-start';
      } else if (right) {
        justifyContent = 'flex-end';
      }
    }

    if (spaceBetween) {
      justifyContent = 'space-between';
    }

    useEffect(() => {
      if (debugBorder) {
        // eslint-disable-next-line no-console
        console.warn('Using debugBorder in Container. Do not push this to production!');
      }
    }, [debugBorder]);

    const containerSx: SxProps<Theme> = {
      m,
      mt,
      mb,
      ml,
      mr,
      mx,
      my,
      p,
      pt,
      pb,
      pl,
      pr,
      px,
      py,
      ...(fullWidth ? { width: '100%' } : {}),
      ...(fullHeight ? { height: '100%' } : {}),
      display: 'flex',
      flexFlow: `${column ? 'column' : 'row'} ${wrap ? 'wrap' : 'nowrap'}`,
      gap,
      justifyContent,
      alignItems,
      ...(debugBorder ? { border: '1px solid black' } : {}),
    };

    const mergedSx: SxProps<Theme> = Array.isArray(sx) ? [containerSx, ...sx] : [containerSx, ...(sx ? [sx] : [])];

    return (
      <Box ref={ref} sx={mergedSx} {...boxProps}>
        {children}
      </Box>
    );
  },
);

Container.displayName = 'Container';

export default Container;
